/**
 * Sticky Add to Cart Button
 * Creates a sticky version of the add to cart button that appears when scrolling
 */

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.originalButton = null;
    this.stickyContainer = null;
    this.isVisible = false;
    this.observer = null;
    this.productForm = null;
    this.variantIdInput = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Find the original add to cart button
    this.originalButton = document.querySelector('.product-form__submit');
    if (!this.originalButton) return;

    // Find the product form
    this.productForm = document.querySelector('product-form');
    if (!this.productForm) return;

    // Create sticky container
    this.createStickyContainer();

    // Setup intersection observer
    this.setupObserver();

    // Listen for variant changes
    this.setupVariantListener();

    // Listen for cart updates
    this.setupCartListener();
    
    // Listen for bundle updates
    this.setupBundleListener();
  }

  createStickyContainer() {
    // Create the sticky container element
    this.stickyContainer = document.createElement('div');
    this.stickyContainer.className = 'sticky-add-to-cart';
    this.stickyContainer.setAttribute('aria-hidden', 'true');
    
    // Create inner wrapper
    const innerWrapper = document.createElement('div');
    innerWrapper.className = 'sticky-add-to-cart__inner page-width';
    
    // Create product info section
    const productInfo = document.createElement('div');
    productInfo.className = 'sticky-add-to-cart__info';
    
    // Add product image
    const productImage = this.getCurrentProductImage();
    if (productImage) {
      const img = document.createElement('img');
      img.src = productImage.src;
      img.alt = productImage.alt;
      img.className = 'sticky-add-to-cart__image';
      img.loading = 'lazy';
      productInfo.appendChild(img);
    }
    
    // Add product title and price
    const titleElement = document.querySelector('.product__title h1');
    const priceElement = document.querySelector('.price__regular .price-item--regular') || 
                        document.querySelector('.price__sale .price-item--sale');
    
    if (titleElement) {
      const title = document.createElement('h3');
      title.className = 'sticky-add-to-cart__title';
      title.textContent = titleElement.textContent.trim();
      productInfo.appendChild(title);
    }
    
    // Create price container that can handle bundle pricing
    const priceContainer = document.createElement('div');
    priceContainer.className = 'sticky-add-to-cart__price-container';
    
    if (priceElement) {
      const price = document.createElement('span');
      price.className = 'sticky-add-to-cart__price';
      price.innerHTML = priceElement.innerHTML;
      priceContainer.appendChild(price);
    }
    
    productInfo.appendChild(priceContainer);
    
    // Create button container
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'sticky-add-to-cart__button-container';
    
    // Clone the original button
    const stickyButton = this.originalButton.cloneNode(true);
    stickyButton.removeAttribute('id');
    stickyButton.className = 'sticky-add-to-cart__button product-form__submit button button--full-width';
    
    // Store original text for later use
    stickyButton.setAttribute('data-add-to-cart-text', this.originalButton.textContent.trim());
    
    // Handle button click
    stickyButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleAddToCart();
    });
    
    buttonContainer.appendChild(stickyButton);
    
    // Assemble the sticky container
    innerWrapper.appendChild(productInfo);
    innerWrapper.appendChild(buttonContainer);
    this.stickyContainer.appendChild(innerWrapper);
    
    // Append to body
    document.body.appendChild(this.stickyContainer);
  }

  setupObserver() {
    // Options for the intersection observer
    const options = {
      root: null,
      rootMargin: '-100px 0px 0px 0px',
      threshold: 0
    };

    // Create the observer
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting && window.scrollY > 300) {
          this.show();
        } else {
          this.hide();
        }
      });
    }, options);

    // Start observing the original button
    this.observer.observe(this.originalButton);
  }

  setupVariantListener() {
    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      this.updateStickyButton(event.detail);
    });

    // Listen for PubSub variant changes if available
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, (variant) => {
        this.updateStickyButton(variant);
      });
    }
    
    // Also listen for custom variant picker changes
    const variantRadios = document.querySelectorAll('input[name="id"]');
    variantRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        setTimeout(() => {
          this.syncButtonStates();
          this.updateStickyImage();
        }, 100);
      });
    });
    
    // Watch for media changes
    this.watchMediaChanges();
  }

  setupCartListener() {
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.updateButtonState();
    });
    
    // Listen for PubSub cart updates
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        this.updateButtonState();
      });
    }

    // Listen for form state changes
    if (this.productForm) {
      this.productForm.addEventListener('product-form:state-change', (event) => {
        this.updateButtonState();
      });
    }
  }
  
  setupBundleListener() {
    // Watch for bundle pricing updates
    if (window.bundlePricing) {
      // Use MutationObserver to watch for button text changes
      const observer = new MutationObserver(() => {
        this.syncButtonStates();
        this.updateBundlePrice();
      });
      
      if (this.originalButton) {
        observer.observe(this.originalButton, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
    }
  }
  
  updateBundlePrice() {
    // Update the price display in sticky bar when bundle changes
    if (window.bundlePricing && window.bundlePricing.currentBundle) {
      const priceContainer = this.stickyContainer.querySelector('.sticky-add-to-cart__price-container');
      if (!priceContainer) return;
      
      const bundle = window.bundlePricing.currentBundle;
      const total = bundle.discountedPrice * bundle.quantity;
      const retailTotal = bundle.basePrice * bundle.quantity;
      const hasDiscount = total < retailTotal;
      
      let priceHTML = '';
      if (hasDiscount) {
        priceHTML = `
          <span class="sticky-add-to-cart__price sticky-add-to-cart__price--compare">
            <s>${window.Shopify.formatMoney ? window.Shopify.formatMoney(retailTotal, window.Shopify.money_format || '${{amount}}') : '$' + retailTotal.toFixed(2)}</s>
          </span>
          <span class="sticky-add-to-cart__price sticky-add-to-cart__price--sale">
            ${window.Shopify.formatMoney ? window.Shopify.formatMoney(total, window.Shopify.money_format || '${{amount}}') : '$' + total.toFixed(2)}
          </span>
        `;
      } else {
        priceHTML = `
          <span class="sticky-add-to-cart__price">
            ${window.Shopify.formatMoney ? window.Shopify.formatMoney(total, window.Shopify.money_format || '${{amount}}') : '$' + total.toFixed(2)}
          </span>
        `;
      }
      
      priceContainer.innerHTML = priceHTML;
    }
  }

  updateStickyButton(variant) {
    const stickyButton = this.stickyContainer.querySelector('.sticky-add-to-cart__button');
    const priceElement = this.stickyContainer.querySelector('.sticky-add-to-cart__price');
    const stickyImage = this.stickyContainer.querySelector('.sticky-add-to-cart__image');
    
    if (!stickyButton) return;

    // Update button state based on variant availability
    if (variant) {
      if (variant.available) {
        stickyButton.disabled = false;
        stickyButton.textContent = stickyButton.getAttribute('data-add-to-cart-text') || 'Add to cart';
      } else {
        stickyButton.disabled = true;
        stickyButton.textContent = 'Sold out';
      }

      // Update price if it changes with variant
      if (priceElement && variant.price) {
        const formattedPrice = Shopify.formatMoney(variant.price, window.Shopify.money_format || '${{amount}}');
        priceElement.innerHTML = formattedPrice;
      }
      
      // Update image if variant has a featured image
      if (stickyImage && variant.featured_image) {
        stickyImage.src = variant.featured_image.src;
        stickyImage.alt = variant.featured_image.alt || variant.title;
      }
    }

    // Sync with original button state
    this.syncButtonStates();
  }

  updateButtonState() {
    this.syncButtonStates();
  }
  
  watchMediaChanges() {
    // Watch for changes in the active media
    const mediaWrapper = document.querySelector('.product__media-wrapper');
    if (!mediaWrapper) return;
    
    // Create observer to watch for active media changes
    const observer = new MutationObserver(() => {
      this.updateStickyImage();
    });
    
    observer.observe(mediaWrapper, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });
  }
  
  getCurrentProductImage() {
    // Try to find the currently active/selected image
    let currentImage = null;
    
    // Method 1: Look for active thumbnail
    const activeThumbnail = document.querySelector('.thumbnail-list__item.is-active img, .thumbnail-list__item[aria-current="true"] img');
    if (activeThumbnail) {
      currentImage = activeThumbnail;
    }
    
    // Method 2: Look for the main displayed image
    if (!currentImage) {
      const mainImage = document.querySelector('.product__media.is-active img, .product__modal-opener--image.is-active img, .product__media-item.is-active img');
      if (mainImage) {
        currentImage = mainImage;
      }
    }
    
    // Method 3: Look for the first visible image in the media gallery
    if (!currentImage) {
      const visibleImage = document.querySelector('.product__media img:not(.skip-lazy)');
      if (visibleImage) {
        currentImage = visibleImage;
      }
    }
    
    return currentImage;
  }
  
  updateStickyImage() {
    const stickyImage = this.stickyContainer.querySelector('.sticky-add-to-cart__image');
    if (!stickyImage) return;
    
    const currentImage = this.getCurrentProductImage();
    
    // Update the sticky image if we found a current image
    if (currentImage && currentImage.src) {
      stickyImage.src = currentImage.src;
      stickyImage.alt = currentImage.alt || '';
    }
  }

  syncButtonStates() {
    const stickyButton = this.stickyContainer.querySelector('.sticky-add-to-cart__button');
    if (stickyButton && this.originalButton) {
      stickyButton.disabled = this.originalButton.disabled;
      
      // Check if this is a bundle product with custom pricing display
      const isBundleProduct = window.bundlePricing && this.originalButton.innerHTML.includes('text-decoration: line-through');
      
      if (isBundleProduct) {
        // Copy the full HTML to preserve bundle pricing display
        stickyButton.innerHTML = this.originalButton.innerHTML;
      } else {
        stickyButton.textContent = this.originalButton.textContent;
      }
      
      // Copy loading state
      if (this.originalButton.classList.contains('loading')) {
        stickyButton.classList.add('loading');
      } else {
        stickyButton.classList.remove('loading');
      }
    }
  }

  handleAddToCart() {
    // Find the product form and submit it
    if (this.productForm) {
      // Trigger click on the original button to maintain all original functionality
      this.originalButton.click();
      
      // Sync button states immediately
      this.syncButtonStates();
    }
  }

  show() {
    if (!this.isVisible) {
      this.stickyContainer.classList.add('is-visible');
      this.stickyContainer.setAttribute('aria-hidden', 'false');
      this.isVisible = true;
      
      // Sync button state when showing
      this.syncButtonStates();
      
      // Update the image to match current variant
      this.updateStickyImage();
      
      // Update bundle price if applicable
      if (window.bundlePricing) {
        this.updateBundlePrice();
      }
    }
  }

  hide() {
    if (this.isVisible) {
      this.stickyContainer.classList.remove('is-visible');
      this.stickyContainer.setAttribute('aria-hidden', 'true');
      this.isVisible = false;
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.stickyContainer) {
      this.stickyContainer.remove();
    }
  }
}

// Define the custom element
customElements.define('sticky-add-to-cart', StickyAddToCart);

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  // Only initialize on product pages and not on checkout
  const isProductPage = document.querySelector('.product-form__submit');
  const isCheckout = window.location.pathname.includes('/checkouts/');
  const isCart = window.location.pathname.includes('/cart');
  
  if (isProductPage && !isCheckout && !isCart) {
    const stickyElement = document.createElement('sticky-add-to-cart');
    document.body.appendChild(stickyElement);
  }
});